import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { company, loanPrograms, residentialPrograms } from "../mock";
import { useToast } from "../hooks/use-toast";
import api from "../lib/api";

export default function Contact() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", program: "", message: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please complete required fields", description: "Name, email and message are required." });
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSubmitted(true);
      toast({ title: "Message sent!", description: "Our team will reach out to you shortly." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: Phone, label: "Call Us", value: company.phoneDisplay, href: `tel:${company.phoneTel}` },
    { icon: Mail, label: "Email Us", value: company.email, href: `mailto:${company.email}` },
    { icon: MapPin, label: "Location", value: company.address },
    { icon: Clock, label: "Hours", value: company.hours },
  ];

  return (
    <main>
      <PageHero
        title="Contact Us"
        crumb="Contact"
        subtitle="Have a scenario in mind? Reach out for a free, no-obligation consultation."
      />

      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-5">
          {/* Info */}
          <Reveal className="lg:col-span-2">
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0b1f3a]">Get In Touch</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Whether you're securing working capital or financing an investment property, our team is
              ready to help you close quickly and securely.
            </p>
            <div className="mt-8 space-y-5">
              {info.map((it) => (
                <div key={it.label} className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl bg-[#0b1f3a] flex items-center justify-center shrink-0">
                    <it.icon className="h-5 w-5 text-[#c6a15b]" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">{it.label}</div>
                    {it.href ? (
                      <a href={it.href} className="font-semibold text-[#0b1f3a] hover:text-[#b08e46] transition break-all">{it.value}</a>
                    ) : (
                      <div className="font-semibold text-[#0b1f3a]">{it.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={120} className="lg:col-span-3">
            <div className="rounded-2xl bg-white p-8 sm:p-10 shadow-sm ring-1 ring-slate-100">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="mx-auto h-16 w-16 rounded-full bg-[#c6a15b]/15 flex items-center justify-center">
                    <CheckCircle2 className="h-8 w-8 text-[#b08e46]" />
                  </div>
                  <h3 className="font-display font-bold text-2xl text-[#0b1f3a] mt-6">Thank You!</h3>
                  <p className="mt-3 text-slate-600">Your message has been received. A member of our team will contact you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                      <input value={form.name} onChange={update("name")} className="w-full rounded-lg border border-slate-200 px-4 py-3 focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition" placeholder="John Smith" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                      <input value={form.phone} onChange={update("phone")} className="w-full rounded-lg border border-slate-200 px-4 py-3 focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition" placeholder="(555) 000-0000" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                      <input type="email" value={form.email} onChange={update("email")} className="w-full rounded-lg border border-slate-200 px-4 py-3 focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition" placeholder="you@company.com" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Loan Program</label>
                      <select value={form.program} onChange={update("program")} className="w-full rounded-lg border border-slate-200 px-4 py-3 focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition bg-white">
                        <option value="">Select a program</option>
                        <optgroup label="Commercial">
                          {loanPrograms.map((p) => (
                            <option key={p.slug} value={p.title}>{p.title}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Residential">
                          {residentialPrograms.map((p) => (
                            <option key={p.slug} value={p.title}>{p.title}</option>
                          ))}
                        </optgroup>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Message *</label>
                    <textarea value={form.message} onChange={update("message")} rows={5} className="w-full rounded-lg border border-slate-200 px-4 py-3 focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition resize-none" placeholder="Tell us about your financing needs..." />
                  </div>
                  <button type="submit" className="inline-flex items-center gap-2 rounded-md bg-[#c6a15b] px-7 py-3.5 font-bold text-[#0b1f3a] hover:bg-[#d8bc85] transition-colors">
                    Send Message <Send className="h-4 w-4" />
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
